import { Button } from '@isbj-redesign/frontend-components'
import DownloadIcon from '@isbj-redesign/icons/FaFileDownloadSolid'
import { useNotifications } from '@isbj-redesign/shared-components'
import React, { useCallback, useMemo } from 'react'
import { QueryObserverResult, RefetchOptions } from 'react-query'
import { useMutateBereitstellungDaten } from './Api'
import { useEncryption } from './EncryptionProvider'
import TimeRemaining from './TimeRemaining'
import { BereitstellungListItem, ErrorLike, Seite } from './types'
import { useSaveFile } from './utils'

export type HerunterladenButtonProps = {
    bereitstellung: BereitstellungListItem
    refetch: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<Seite<BereitstellungListItem>, ErrorLike>>
}

export default function HerunterladenButton({ bereitstellung, refetch }: HerunterladenButtonProps) {
    const { addErrorNotification } = useNotifications()
    const { mutateAsync } = useMutateBereitstellungDaten(encodeURI(bereitstellung.id))
    const { decrypt, schluesselUuid: uuid } = useEncryption()
    const saveFile = useSaveFile()
    const disabled = useMemo(() => bereitstellung.schluesselUuid !== uuid, [bereitstellung, uuid])

    const onClick = useCallback(async () => {
        const response = await (await mutateAsync()).text()
        try {
            const { plaintext } = await decrypt(response)
            const decryptedBlob = new Blob([plaintext], { type: bereitstellung.typ })
            saveFile(decryptedBlob, bereitstellung.dateiname)
            refetch()
        } catch (e) {
            addErrorNotification(`Das Dokument '${bereitstellung.name}' konnte nicht entschlüsselt werden. Bitte fordern Sie das Dokument erneut an.`)
        }
    }, [mutateAsync, bereitstellung.dateiname, bereitstellung.typ, decrypt, saveFile, addErrorNotification, bereitstellung.name, refetch])

    return (
        <div className="d-inline-flex flex-column align-items-center">
            <Button
                disabled={disabled}
                title={disabled ? 'Entschlüsselung nicht möglich. Bitte fordern Sie dieses Dokument erneut an.' : undefined}
                onClick={onClick}
            >
                <DownloadIcon className="mr-1" /> Herunterladen
            </Button>
            <small>
                noch <TimeRemaining target={bereitstellung.gueltigBis} />
            </small>
        </div>
    )
}
